import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { formatApprovalDateLabel, GROUP_BILLING_STATUS_LABELS } from "@shared/groupBilling";
import { Link } from "wouter";

const money = new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" });

type Overview = {
  billingGroupId: number;
  customerId?: number;
  locked: boolean;
  lockedReason: string | null;
  members: Array<{
    proposalId: number;
    proposalRef: string;
    proposalName: string | null;
    approvedAt: Date | string | null;
    billingStatus: string;
    billingStatusLabel: string;
    amountSgd: number;
    approved: boolean;
  }>;
  totalGroupValue: number;
  approvedValue: number;
  pendingValue: number;
  approvedCount: number;
  pendingCount: number;
  allApproved: boolean;
  readyForBilling: boolean;
  unbilledApprovedCount: number;
  activePayableInvoices?: Array<{
    dealInvoiceId: number;
    invoiceNumber: string | null;
    invoiceStatus: string;
    finalAmount: string | null;
    invoiceToken: string | null;
    allocationCount: number;
  }>;
  billingEventHistory?: Array<{
    eventId: number;
    kind: string;
    createdAt: Date | string | null;
    dealInvoiceIds: number[];
    totalAmountSgd: string | null;
    isLegacyPerProposalEvent: boolean;
    label: string;
  }>;
};

type Props = {
  groups: Overview[];
  onRefresh?: () => void;
  /** Highlight a specific group (e.g. from deal page). */
  focusGroupId?: number | null;
  /** Hide invoice-generation actions when the viewer cannot mutate this customer. */
  canGenerate?: boolean;
};

function newClientIdempotencyToken(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function CustomerBillingGroupPanel({ groups, onRefresh, focusGroupId, canGenerate = true }: Props) {
  const utils = trpc.useUtils();
  const [confirm, setConfirm] = useState<{
    group: Overview;
    mode: "full_group" | "approved_only";
  } | null>(null);
  const [deliveryResult, setDeliveryResult] = useState<{
    invoiceCount: number;
    reused: boolean;
    customerId: number | null;
    invoiceNumber: string | null;
    invoiceUrl: string | null;
    totalAmountSgd: number | null;
    deliveryFailed: boolean;
  } | null>(null);
  const idempotencyTokenRef = useRef<string>(newClientIdempotencyToken());
  const pendingCustomerIdRef = useRef<number | null>(null);

  const openConfirm = (group: Overview, mode: "full_group" | "approved_only") => {
    idempotencyTokenRef.current = newClientIdempotencyToken();
    pendingCustomerIdRef.current = group.customerId ?? null;
    setConfirm({ group, mode });
  };

  const generate = trpc.proposals.generateGroupBillingEvent.useMutation({
    onSuccess: (data) => {
      const n = data.includedProposalIds?.length ?? 0;
      const invoiceNumber =
        (data as { invoiceNumber?: string | null }).invoiceNumber ??
        data.allocations?.[0]?.invoiceNumber ??
        null;
      const invoiceUrl = (data as { invoiceUrl?: string | null }).invoiceUrl ?? null;
      setConfirm(null);
      setDeliveryResult({
        invoiceCount: n,
        reused: Boolean(data.reusedExistingResult),
        customerId: pendingCustomerIdRef.current,
        invoiceNumber,
        invoiceUrl,
        totalAmountSgd: Number(data.totalAmountSgd ?? 0) || null,
        deliveryFailed: false,
      });
      onRefresh?.();
      void utils.proposals.listCustomerBillingGroups.invalidate();
      void utils.proposals.getBillingGroupForProposal.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const visible = useMemo(() => {
    if (focusGroupId == null) return groups;
    return groups.filter((g) => g.billingGroupId === focusGroupId);
  }, [groups, focusGroupId]);

  if (visible.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-sm text-muted-foreground text-center">
          No billing groups for this customer yet. Enable Group Billing on a proposal under Commercial Settings.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {visible.map((group) => {
        const unbilledApproved = group.members.filter(
          (m) => m.approved && m.billingStatus === "approved_awaiting_billing",
        );
        const pending = group.members.filter((m) => !m.approved);
        const showFull = canGenerate && group.allApproved && group.unbilledApprovedCount > 0;
        const showPartial = canGenerate && !group.allApproved && unbilledApproved.length > 0;

        return (
          <Card key={group.billingGroupId}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-sm">Billing group #{group.billingGroupId}</CardTitle>
                <Badge variant="outline" className="text-[10px]">
                  {group.approvedCount} of {group.members.length} approved
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {group.allApproved && group.unbilledApprovedCount > 0 ? (
                <p className="text-sm font-medium text-emerald-800">Ready for Billing</p>
              ) : null}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b">
                      <th className="py-2 pr-3 font-medium">Proposal</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 pr-3 font-medium">Approval Date</th>
                      <th className="py-2 pr-3 font-medium">Billing Status</th>
                      <th className="py-2 font-medium text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.members.map((m) => (
                      <tr key={m.proposalId} className="border-b last:border-0">
                        <td className="py-2 pr-3">
                          <Link href={`/sales/proposals/${m.proposalId}/deal`} className="font-medium hover:underline">
                            {m.proposalRef}
                          </Link>
                          {m.proposalName ? (
                            <p className="text-xs text-muted-foreground">{m.proposalName}</p>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3">{m.approved ? "Approved" : "Awaiting Approval"}</td>
                        <td className="py-2 pr-3">{formatApprovalDateLabel(m.approvedAt) ?? "—"}</td>
                        <td className="py-2 pr-3">
                          {GROUP_BILLING_STATUS_LABELS[m.billingStatus as keyof typeof GROUP_BILLING_STATUS_LABELS] ??
                            m.billingStatusLabel}
                        </td>
                        <td className="py-2 text-right tabular-nums">{money.format(m.amountSgd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(group.activePayableInvoices?.length ?? 0) > 0 ? (
                <div className="rounded-md border bg-muted/30 px-3 py-2 space-y-2">
                  <p className="text-xs font-medium text-foreground">Active payable invoice</p>
                  {(group.activePayableInvoices ?? []).map((inv) => (
                    <div key={inv.dealInvoiceId} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <div>
                        <p className="font-mono font-medium">{inv.invoiceNumber ?? `deal-${inv.dealInvoiceId}`}</p>
                        <p className="text-xs text-muted-foreground">
                          {inv.allocationCount} allocation{inv.allocationCount === 1 ? "" : "s"} ·{" "}
                          {inv.finalAmount != null
                            ? money.format(Number(inv.finalAmount))
                            : "—"}{" "}
                          · {inv.invoiceStatus}
                        </p>
                      </div>
                      {inv.invoiceToken ? (
                        <Button type="button" size="sm" variant="outline" asChild>
                          <a href={`/api/deal-invoices/${inv.invoiceToken}/invoice.pdf`} target="_blank" rel="noopener noreferrer">
                            View Invoice
                          </a>
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              {(group.billingEventHistory?.length ?? 0) > 0 ? (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Billing event history</p>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {(group.billingEventHistory ?? []).map((ev) => (
                      <li key={ev.eventId} className="flex flex-wrap items-center gap-2">
                        <span>{ev.label}</span>
                        {ev.isLegacyPerProposalEvent ? (
                          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-900 border-amber-200">
                            Legacy
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">
                            Active path
                          </Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="grid gap-1 text-sm">
                <p>
                  <span className="text-muted-foreground">Group total (collectible deposits): </span>
                  {money.format(group.totalGroupValue)}
                </p>
                {!group.allApproved ? (
                  <>
                    <p>
                      <span className="text-muted-foreground">Approved value: </span>
                      {money.format(group.approvedValue)}
                    </p>
                    <p>
                      <span className="text-muted-foreground">Pending value: </span>
                      {money.format(group.pendingValue)}
                    </p>
                  </>
                ) : null}
              </div>

              <div className="flex flex-wrap gap-2">
                {showFull ? (
                  <Button
                    type="button"
                    size="sm"
                    disabled={generate.isPending}
                    onClick={() => openConfirm(group, "full_group")}
                  >
                    Generate Consolidated Invoice
                  </Button>
                ) : null}
                {showPartial ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={generate.isPending}
                      onClick={() => openConfirm(group, "approved_only")}
                    >
                      Bill Approved Proposals Now
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled
                      title="All proposals must be approved"
                    >
                      Generate Consolidated Invoice
                    </Button>
                  </>
                ) : null}
              </div>
              {showPartial ? (
                <p className="text-xs text-muted-foreground">
                  {pending.length} proposal{pending.length === 1 ? "" : "s"} still awaiting approval. Approved
                  proposals will be included on one consolidated invoice when billed.
                </p>
              ) : null}
            </CardContent>
          </Card>
        );
      })}

      <AlertDialog open={confirm != null} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.mode === "full_group"
                ? "Generate Consolidated Invoice?"
                : "Bill approved proposals now?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  This will create one consolidated invoice covering the approved proposals below (with a line
                  item per proposal).
                </p>
                {confirm ? (
                  <>
                    <div>
                      <p className="font-medium text-foreground mb-1">Included:</p>
                      <ul className="space-y-1">
                        {confirm.group.members
                          .filter((m) => m.approved && m.billingStatus === "approved_awaiting_billing")
                          .map((m) => (
                            <li key={m.proposalId}>
                              ✓ {m.proposalName || m.proposalRef} ({money.format(m.amountSgd)})
                            </li>
                          ))}
                      </ul>
                    </div>
                    {confirm.mode === "approved_only" ? (
                      <div>
                        <p className="font-medium text-foreground mb-1">Excluded:</p>
                        <ul className="space-y-1">
                          {confirm.group.members
                            .filter((m) => !m.approved)
                            .map((m) => (
                              <li key={m.proposalId}>
                                ○ {m.proposalName || m.proposalRef} (Awaiting Approval)
                              </li>
                            ))}
                        </ul>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={generate.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={generate.isPending || !confirm}
              onClick={(e) => {
                e.preventDefault();
                if (!confirm) return;
                generate.mutate({
                  billingGroupId: confirm.group.billingGroupId,
                  mode: confirm.mode,
                  idempotencyKey: idempotencyTokenRef.current,
                });
              }}
            >
              {generate.isPending ? "Generating…" : "Generate Consolidated Invoice"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deliveryResult != null}
        onOpenChange={(open) => !open && setDeliveryResult(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deliveryResult?.reused
                ? "Consolidated invoice already created"
                : "Consolidated invoice created"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  {deliveryResult?.reused
                    ? "Consolidated invoice already exists for this request."
                    : "Consolidated invoice created and sent."}
                </p>
                {deliveryResult?.invoiceNumber ? (
                  <p>
                    Invoice: <span className="font-mono font-medium text-foreground">{deliveryResult.invoiceNumber}</span>
                  </p>
                ) : null}
                {deliveryResult?.totalAmountSgd != null ? (
                  <p>
                    Total:{" "}
                    <span className="font-medium text-foreground tabular-nums">
                      {money.format(deliveryResult.totalAmountSgd)}
                    </span>
                  </p>
                ) : null}
                <p className="text-xs">
                  One invoice covering {deliveryResult?.invoiceCount ?? 0} proposal
                  {(deliveryResult?.invoiceCount ?? 0) === 1 ? "" : "s"}.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {deliveryResult?.invoiceUrl ? (
              <Button type="button" variant="outline" asChild>
                <a href={deliveryResult.invoiceUrl} target="_blank" rel="noopener noreferrer">
                  View Invoice
                </a>
              </Button>
            ) : deliveryResult?.customerId ? (
              <Button type="button" variant="outline" asChild>
                <a href={`/sales/customers/${deliveryResult.customerId}?tab=purchase`}>View Invoice</a>
              </Button>
            ) : null}
            <AlertDialogAction onClick={() => setDeliveryResult(null)}>Done</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
